import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ComposedChart,
  LineChart,
  AreaChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  Cell
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import io from 'socket.io-client';
import './embed.css';

const EmbedStockChart = () => {
  const { ticker } = useParams();
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#0a0a0a' : '#ffffff');
  const chartType = searchParams.get('type') || 'area'; // line, area, candle
  const timeRange = searchParams.get('range') || '1h';
  const showVolume = searchParams.get('volume') !== 'false';
  const showHeader = searchParams.get('header') !== 'false';
  const accentColor = searchParams.get('accent') || '#00ff88';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;

  const [stockData, setStockData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Interval mapping for API
  const intervalMapping = {
    '5m': '1m',
    '15m': '1m',
    '30m': '1m',
    '1h': '5m',
    '4h': '15m',
    '1d': '30m',
    '1w': '1h'
  };

  const fetchData = async () => {
    try {
      // Fetch stock info
      const stockResponse = await fetch(`${API_BASE_URL}/api/stocks/ticker/${ticker}`);
      if (!stockResponse.ok) {
        // Don't throw — show placeholder and retry on next interval
        setLoading(false);
        return;
      }
      const stock = await stockResponse.json();
      setStockData(stock);

      // Fetch chart data
      const interval = intervalMapping[timeRange] || '5m';
      const limit = timeRange === '5m' ? 60 : timeRange === '15m' ? 180 : 100;

      const historyResponse = await fetch(
        `${API_BASE_URL}/api/stocks/${stock.id}/history?interval=${interval}&limit=${limit}`
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        const formattedData = historyData.data.map((candle, index) => ({
          index,
          time: new Date(candle.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          }),
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          price: candle.close,
          volume: candle.volume
        }));
        setChartData(formattedData);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching embed data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Socket for live price updates
    const socket = io(API_BASE_URL, { auth: { public: true } });
    socket.on('stock-updates', (updates) => {
      const stocksArray = Array.isArray(updates) ? updates : updates?.stocks;
      if (!stocksArray) return;
      const match = stocksArray.find(u => u.ticker?.toUpperCase() === ticker?.toUpperCase());
      if (!match) return;
      const livePrice = parseFloat(match.currentPrice || match.price);
      if (!livePrice || isNaN(livePrice)) return;

      // Update header price + percentage
      setStockData(prev => {
        if (!prev) return prev;
        const basePrice = prev.basePrice || prev.currentPrice;
        const changePercent24h = basePrice > 0 ? ((livePrice - basePrice) / basePrice) * 100 : 0;
        return { ...prev, currentPrice: livePrice, changePercent24h };
      });

      // Patch last candle with live price
      setChartData(prev => {
        if (!prev || prev.length === 0) return prev;
        const updated = [...prev];
        const last = { ...updated[updated.length - 1] };
        last.close = livePrice;
        last.price = livePrice;
        last.high = Math.max(last.high, livePrice);
        last.low = Math.min(last.low, livePrice);
        updated[updated.length - 1] = last;
        return updated;
      });
    });

    const interval = setInterval(fetchData, refreshInterval);
    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [ticker, timeRange, refreshInterval]);

  if (loading) {
    return (
      <div className="embed-container" style={{ background: bg }}>
        <div className="embed-loading">Loading {ticker}...</div>
      </div>
    );
  }

  if (error || (!stockData && !loading)) {
    return (
      <div className="embed-container embed-chart" style={{ background: bg }}>
        {showHeader && (
          <div className="embed-header">
            <div className="embed-ticker">{ticker?.toUpperCase() || '---'}</div>
            <div className="embed-price-info">
              <span className="embed-price" style={{ color: '#555' }}>--</span>
              <span className="embed-change" style={{ color: '#555' }}>--.--% </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  const prices = chartData.map(d => d.close).filter(p => p != null);
  const minPrice = prices.length > 0 ? Math.min(...prices) * 0.998 : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) * 1.002 : 100;

  const priceChange = stockData?.changePercent24h || 0;
  const isPositive = priceChange >= 0;
  const lineColor = isPositive ? '#00ff88' : '#ff4444';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="embed-tooltip">
          <div className="tooltip-price">{formatCurrency(data.close || data.price)}</div>
          <div className="tooltip-time">{data.time}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`embed-container embed-chart ${theme}`}
      style={{ background: bg === 'transparent' ? 'transparent' : bg }}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      {showHeader && stockData && (
        <div className="embed-header">
          <div className="embed-ticker">{stockData.ticker}</div>
          <div className="embed-price-info">
            <span className="embed-price">{formatCurrency(stockData.currentPrice)}</span>
            <span className={`embed-change ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
        </div>
      )}

      <div className="embed-chart-area" style={{ height: showHeader ? 'calc(100% - 50px)' : '100%' }}>
        <ResponsiveContainer width="100%" height={showVolume ? '80%' : '100%'}>
          {chartType === 'line' ? (
            <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: '#888' }}
                axisLine={{ stroke: '#333' }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[minPrice, maxPrice]}
                tick={{ fontSize: 10, fill: '#888' }}
                axisLine={{ stroke: '#333' }}
                tickFormatter={v => formatCurrency(v)}
                orientation="right"
                width={60}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="close"
                stroke={lineColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: lineColor }}
              />
            </LineChart>
          ) : (
            <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`gradient-${ticker}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={lineColor} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={lineColor} stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: '#888' }}
                axisLine={{ stroke: '#333' }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[minPrice, maxPrice]}
                tick={{ fontSize: 10, fill: '#888' }}
                axisLine={{ stroke: '#333' }}
                tickFormatter={v => formatCurrency(v)}
                orientation="right"
                width={60}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="close"
                stroke={lineColor}
                strokeWidth={2}
                fill={`url(#gradient-${ticker})`}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>

        {showVolume && chartData.length > 0 && (
          <ResponsiveContainer width="100%" height="20%">
            <ComposedChart data={chartData} margin={{ top: 0, right: 10, bottom: 0, left: 0 }}>
              <XAxis dataKey="time" hide />
              <YAxis hide />
              <Bar dataKey="volume" fill="rgba(255,255,255,0.2)">
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.close >= entry.open ? 'rgba(0,255,136,0.3)' : 'rgba(255,68,68,0.3)'}
                  />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default EmbedStockChart;
